import * as React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Plus, ChevronRight, ChevronDown, FileSpreadsheet, FileText, Search, X, Check, GripVertical } from "lucide-react";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/use-toast";
import { useSupabaseSync } from "@/hooks/useSupabaseSync";
import { useAuth } from "@/context/AuthContext";
import { useFinance } from "@/context/FinanceContext";
import { ImportChartOfAccountsButton } from "@/components/ImportChartOfAccountsButton";
import { useChartOfAccountsExport } from "@/hooks/useChartOfAccountsExport";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { PermissionAlert } from "@/components/PermissionAlert";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";


interface Level1Data {
  id: string;
  code: string;
  name: string;
  color?: string;
}

interface Level2Data {
  id: string;
  level1Id: string;
  code: string;
  name: string;
  type: "receita" | "despesa";
  color?: string;
}

interface Level3Data {
  id: string;
  level2Id?: string;
  code?: string;
  name: string;
  type: "receita" | "despesa";
  color?: string;
  hidden?: boolean;
}

// DnD modifier to restrict to vertical axis
const restrictToVerticalAxisModifier = ({ transform }: any) => {
  return { ...transform, x: 0 };
};

// ========================================================================
// Module-scope subcomponents (extracted to keep component identity stable
// across parent re-renders — otherwise inputs lose focus on every keystroke)
// ========================================================================

interface InlineInputProps {
  inlineName: string;
  setInlineName: (v: string) => void;
  inlineType: "receita" | "despesa";
  setInlineType: (v: "receita" | "despesa") => void;
  inputRef: React.RefObject<HTMLInputElement>;
  onSave: () => void;
  onCancel: () => void;
  showType?: boolean;
}

const InlineInput: React.FC<InlineInputProps> = ({
  inlineName, setInlineName, inlineType, setInlineType, inputRef, onSave, onCancel, showType = false,
}) => (
  <div className="flex items-center gap-2 py-1.5 px-2">
    <Input
      ref={inputRef}
      value={inlineName}
      onChange={e => setInlineName(e.target.value)}
      placeholder="Nome da conta..."
      className="h-8 text-sm flex-1"
      onKeyDown={e => { if (e.key === "Enter") onSave(); if (e.key === "Escape") onCancel(); }}
    />
    {showType && (
      <Select value={inlineType} onValueChange={(v: "receita" | "despesa") => setInlineType(v)}>
        <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="receita">Receita</SelectItem>
          <SelectItem value="despesa">Despesa</SelectItem>
        </SelectContent>
      </Select>
    )}
    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onSave}><Check className="h-4 w-4 text-green-600" /></Button>
    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onCancel}><X className="h-4 w-4 text-destructive" /></Button>
  </div>
);

interface SortableL3Props {
  l3: Level3Data & { level: 3 };
  editingId: string | null;
  editName: string;
  setEditName: (v: string) => void;
  editType: "receita" | "despesa";
  setEditType: (v: "receita" | "despesa") => void;
  editInputRef: React.RefObject<HTMLInputElement>;
  saveEdit: () => void;
  cancelEdit: () => void;
  startEdit: (level: 1 | 2 | 3, id: string, name: string, type?: "receita" | "despesa") => void;
  handleDeleteLevel3: (id: string, name: string) => void;
  txCount: number;
}

const SortableL3: React.FC<SortableL3Props> = ({
  l3, editingId, editName, setEditName, editType, setEditType, editInputRef,
  saveEdit, cancelEdit, startEdit, handleDeleteLevel3, txCount,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: l3.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isEditing = editingId === l3.id;

  return (
    <div ref={setNodeRef} style={style} {...attributes}
      className="flex items-center gap-1 md:gap-2 py-2 hover:bg-muted/50 rounded-sm pl-8 md:pl-16">
      <button className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" {...listeners}>
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="w-4 text-center text-muted-foreground">•</span>
      {isEditing ? (
        <div className="flex items-center gap-2 flex-1">
          <Input ref={editInputRef} value={editName} onChange={e => setEditName(e.target.value)} className="h-7 text-sm flex-1"
            onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }} />
          <Select value={editType} onValueChange={(v: "receita" | "despesa") => setEditType(v)}>
            <SelectTrigger className="h-7 w-[100px] text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="receita">Receita</SelectItem>
              <SelectItem value="despesa">Despesa</SelectItem>
            </SelectContent>
          </Select>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveEdit}><Check className="h-3.5 w-3.5 text-green-600" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={cancelEdit}><X className="h-3.5 w-3.5 text-destructive" /></Button>
        </div>
      ) : (
        <>
          <span className="text-sm md:text-base truncate">{l3.code} - {l3.name}</span>
          <Badge className={`text-xs ml-1 md:ml-2 px-2 py-0.5 flex-shrink-0 ${l3.type === "receita" ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700" : "bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 dark:border-red-700"}`}>
            {l3.type === "receita" ? "R" : "D"}
          </Badge>
          <span className="text-xs text-muted-foreground ml-1 md:ml-2 whitespace-nowrap flex-shrink-0">{txCount} - lançamentos</span>
          <div className="ml-auto flex items-center gap-1 md:gap-2 flex-shrink-0">
            <Button size="icon" variant="outline" className="h-7 w-7 md:h-8 md:w-8" onClick={() => startEdit(3, l3.id, l3.name, l3.type)}>
              <Edit className="h-3.5 w-3.5 md:h-4 md:w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="icon" variant="destructive" className="h-7 w-7 md:h-8 md:w-8">
                  <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Excluir Nível 3</AlertDialogTitle>
                  <AlertDialogDescription>Tem certeza que deseja excluir "{l3.name}"?</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={() => handleDeleteLevel3(l3.id, l3.name)}>Confirmar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </>
      )}
    </div>
  );
};

interface SortableL2Props {
  l2: Level2Data & { level: 2; children: (Level3Data & { level: 3 })[] };
  expandedNodes: Set<string>;
  toggleExpand: (id: string) => void;
  editingId: string | null;
  editName: string;
  setEditName: (v: string) => void;
  editType: "receita" | "despesa";
  setEditType: (v: "receita" | "despesa") => void;
  editInputRef: React.RefObject<HTMLInputElement>;
  saveEdit: () => void;
  cancelEdit: () => void;
  startEdit: (level: 1 | 2 | 3, id: string, name: string, type?: "receita" | "despesa") => void;
  handleDeleteLevel2: (id: string, name: string) => void;
  handleDeleteLevel3: (id: string, name: string) => void;
  addingL3ParentId: string | null;
  setAddingL3ParentId: (v: string | null) => void;
  setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  cancelInlineAdd: () => void;
  handleAddLevel3: (l2Id: string) => void;
  inlineName: string;
  setInlineName: (v: string) => void;
  inlineType: "receita" | "despesa";
  setInlineType: (v: "receita" | "despesa") => void;
  inlineInputRef: React.RefObject<HTMLInputElement>;
  transactionCounts: Map<string, number>;
}

const SortableL2: React.FC<SortableL2Props> = (props) => {
  const {
    l2, expandedNodes, toggleExpand, editingId, editName, setEditName, editType, setEditType,
    editInputRef, saveEdit, cancelEdit, startEdit, handleDeleteLevel2, handleDeleteLevel3,
    addingL3ParentId, setAddingL3ParentId, setExpandedNodes, cancelInlineAdd, handleAddLevel3,
    inlineName, setInlineName, inlineType, setInlineType, inlineInputRef, transactionCounts,
  } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: l2.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isExpanded = expandedNodes.has(l2.id);
  const hasChildren = l2.children.length > 0;
  const isEditing = editingId === l2.id;

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-1 md:gap-2 py-2 hover:bg-muted/50 rounded-sm pl-4 md:pl-9">
        <button className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" {...listeners}>
          <GripVertical className="h-4 w-4" />
        </button>
        <button className="w-5 flex-shrink-0" onClick={() => toggleExpand(l2.id)}>
          {hasChildren ? (isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />) : <span className="w-5" />}
        </button>
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1">
            <Input ref={editInputRef} value={editName} onChange={e => setEditName(e.target.value)} className="h-7 text-sm flex-1"
              onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }} />
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={saveEdit}><Check className="h-3.5 w-3.5 text-green-600" /></Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={cancelEdit}><X className="h-3.5 w-3.5 text-destructive" /></Button>
          </div>
        ) : (
        <>
          <span className="font-semibold text-sm md:text-base cursor-pointer truncate" onClick={() => toggleExpand(l2.id)}>{l2.code} - {l2.name}</span>
          <div className="ml-auto flex items-center gap-1 md:gap-2 flex-shrink-0">
              <Button size="sm" variant="ghost" className="h-7 md:h-8 px-2 md:px-3 text-xs md:text-sm"
                onClick={() => { cancelInlineAdd(); setAddingL3ParentId(l2.id); setExpandedNodes(prev => new Set(prev).add(l2.id)); }}>
                <Plus className="h-4 w-4 md:mr-1" />
                <span className="hidden md:inline">Adicionar Nível 3</span>
              </Button>
              <Button size="icon" variant="outline" className="h-7 w-7 md:h-8 md:w-8" onClick={() => startEdit(2, l2.id, l2.name)}>
                <Edit className="h-3.5 w-3.5 md:h-4 md:w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="icon" variant="destructive" className="h-7 w-7 md:h-8 md:w-8">
                    <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir Nível 2</AlertDialogTitle>
                    <AlertDialogDescription>Tem certeza que deseja excluir "{l2.name}"?</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDeleteLevel2(l2.id, l2.name)}>Confirmar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </div>
      {isExpanded && (
        <SortableContext items={l2.children.map(c => c.id)} strategy={verticalListSortingStrategy}>
          {l2.children.map(l3 => (
            <SortableL3
              key={l3.id}
              l3={l3}
              editingId={editingId}
              editName={editName}
              setEditName={setEditName}
              editType={editType}
              setEditType={setEditType}
              editInputRef={editInputRef}
              saveEdit={saveEdit}
              cancelEdit={cancelEdit}
              startEdit={startEdit}
              handleDeleteLevel3={handleDeleteLevel3}
              txCount={transactionCounts.get(l3.id) || 0}
            />
          ))}
          {addingL3ParentId === l2.id && (
            <div className="pl-8 md:pl-14">
              <InlineInput
                showType
                inlineName={inlineName}
                setInlineName={setInlineName}
                inlineType={inlineType}
                setInlineType={setInlineType}
                inputRef={inlineInputRef}
                onSave={() => handleAddLevel3(l2.id)}
                onCancel={cancelInlineAdd}
              />
            </div>
          )}
        </SortableContext>
      )}
    </div>
  );
};

interface SortableL1Props {
  l1: Level1Data & { level: 1; children: (Level2Data & { level: 2; children: (Level3Data & { level: 3 })[] })[] };
  expandedNodes: Set<string>;
  toggleExpand: (id: string) => void;
  editingId: string | null;
  editName: string;
  setEditName: (v: string) => void;
  editType: "receita" | "despesa";
  setEditType: (v: "receita" | "despesa") => void;
  editInputRef: React.RefObject<HTMLInputElement>;
  saveEdit: () => void;
  cancelEdit: () => void;
  startEdit: (level: 1 | 2 | 3, id: string, name: string, type?: "receita" | "despesa") => void;
  handleDeleteLevel1: (id: string, name: string) => void;
  handleDeleteLevel2: (id: string, name: string) => void;
  handleDeleteLevel3: (id: string, name: string) => void;
  addingL2ParentId: string | null;
  setAddingL2ParentId: (v: string | null) => void;
  addingL3ParentId: string | null;
  setAddingL3ParentId: (v: string | null) => void;
  setExpandedNodes: React.Dispatch<React.SetStateAction<Set<string>>>;
  cancelInlineAdd: () => void;
  handleAddLevel2: (l1Id: string) => void;
  handleAddLevel3: (l2Id: string) => void;
  inlineName: string;
  setInlineName: (v: string) => void;
  inlineType: "receita" | "despesa";
  setInlineType: (v: "receita" | "despesa") => void;
  inlineInputRef: React.RefObject<HTMLInputElement>;
  transactionCounts: Map<string, number>;
}

const SortableL1: React.FC<SortableL1Props> = (props) => {
  const {
    l1, expandedNodes, toggleExpand, editingId, editName, setEditName,
    editInputRef, saveEdit, cancelEdit, startEdit,
    handleDeleteLevel1, addingL2ParentId, setAddingL2ParentId,
    setExpandedNodes, cancelInlineAdd, handleAddLevel2,
    inlineName, setInlineName, inlineType, setInlineType, inlineInputRef,
  } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: l1.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const isExpanded = expandedNodes.has(l1.id);
  const hasChildren = l1.children.length > 0;
  const isEditing = editingId === l1.id;

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div className="flex items-center gap-1 md:gap-2 py-2 md:py-3 hover:bg-muted/50 rounded-sm pl-1 md:pl-2">
        <button className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground" {...listeners}>
          <GripVertical className="h-4 w-4 md:h-5 md:w-5" />
        </button>
        <button className="w-5 md:w-6 flex-shrink-0" onClick={() => toggleExpand(l1.id)}>
          {hasChildren ? (isExpanded ? <ChevronDown className="h-4 w-4 md:h-5 md:w-5" /> : <ChevronRight className="h-4 w-4 md:h-5 md:w-5" />) : <span className="w-5" />}
        </button>
        {isEditing ? (
          <div className="flex items-center gap-2 flex-1">
            <Input ref={editInputRef} value={editName} onChange={e => setEditName(e.target.value)} className="h-8 text-sm flex-1 font-bold"
              onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }} />
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={saveEdit}><Check className="h-4 w-4 text-green-600" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={cancelEdit}><X className="h-4 w-4 text-destructive" /></Button>
          </div>
        ) : (
        <>
          <span className="font-bold text-base md:text-lg cursor-pointer truncate" onClick={() => toggleExpand(l1.id)}>{l1.code} - {l1.name}</span>
          <div className="ml-auto flex items-center gap-1 md:gap-2 flex-shrink-0">
              <Button size="sm" variant="ghost" className="h-7 md:h-8 px-2 md:px-3 text-xs md:text-sm"
                onClick={() => { cancelInlineAdd(); setAddingL2ParentId(l1.id); setExpandedNodes(prev => new Set(prev).add(l1.id)); }}>
                <Plus className="h-4 w-4 md:mr-1" />
                <span className="hidden md:inline">Adicionar Nível 2</span>
              </Button>
              <Button size="icon" variant="outline" className="h-7 w-7 md:h-8 md:w-8" onClick={() => startEdit(1, l1.id, l1.name)}>
                <Edit className="h-3.5 w-3.5 md:h-4 md:w-4" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="icon" variant="destructive" className="h-7 w-7 md:h-8 md:w-8">
                    <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir Nível 1</AlertDialogTitle>
                    <AlertDialogDescription>Tem certeza que deseja excluir "{l1.name}"?</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={() => handleDeleteLevel1(l1.id, l1.name)}>Confirmar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </>
        )}
      </div>
      {isExpanded && (
        <SortableContext items={l1.children.map(c => c.id)} strategy={verticalListSortingStrategy}>
          {l1.children.map(l2 => (
            <SortableL2
              key={l2.id}
              l2={l2}
              {...props}
            />
          ))}
          {addingL2ParentId === l1.id && (
            <div className="pl-4 md:pl-7">
              <InlineInput
                inlineName={inlineName}
                setInlineName={setInlineName}
                inlineType={inlineType}
                setInlineType={setInlineType}
                inputRef={inlineInputRef}
                onSave={() => handleAddLevel2(l1.id)}
                onCancel={cancelInlineAdd}
              />
            </div>
          )}
        </SortableContext>
      )}
    </div>
  );
};

// ========================================================================
// Parent component
// ========================================================================

export const ChartOfAccountsUnifiedManager: React.FC = () => {
  const { user } = useAuth();
  const supabaseSync = useSupabaseSync();
  const { forceRefresh, transactions, addCategory, removeCategory } = useFinance();
  const { exportToExcel, exportToPDF } = useChartOfAccountsExport();
  const { canView, showAlert, setShowAlert, alertAction, loading: permLoading } = usePermissionCheck('chart_of_accounts');

  const [level1Accounts, setLevel1Accounts] = React.useState<Level1Data[]>([]);
  const [level2Accounts, setLevel2Accounts] = React.useState<Level2Data[]>([]);
  const [level3Accounts, setLevel3Accounts] = React.useState<Level3Data[]>([]);

  const [expandedNodes, setExpandedNodes] = React.useState<Set<string>>(new Set());
  const [hierarchySearch, setHierarchySearch] = React.useState("");

  const [addingL1, setAddingL1] = React.useState(false);
  const [addingL2ParentId, setAddingL2ParentId] = React.useState<string | null>(null);
  const [addingL3ParentId, setAddingL3ParentId] = React.useState<string | null>(null);
  const [inlineName, setInlineName] = React.useState("");
  const [inlineType, setInlineType] = React.useState<"receita" | "despesa">("despesa");

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editingLevel, setEditingLevel] = React.useState<1 | 2 | 3 | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editType, setEditType] = React.useState<"receita" | "despesa">("despesa");

  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [activeDragLevel, setActiveDragLevel] = React.useState<1 | 2 | 3 | null>(null);

  const inlineInputRef = React.useRef<HTMLInputElement>(null);
  const editInputRef = React.useRef<HTMLInputElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Transaction counts per categoryId (L3)
  const transactionCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    transactions.forEach(t => {
      if (t.categoryId) {
        counts.set(t.categoryId, (counts.get(t.categoryId) || 0) + 1);
      }
    });
    return counts;
  }, [transactions]);

  React.useEffect(() => { if (user) loadData(); }, [user]);

  React.useEffect(() => {
    const allIds = new Set<string>();
    level1Accounts.forEach(l1 => allIds.add(l1.id));
    level2Accounts.forEach(l2 => allIds.add(l2.id));
    setExpandedNodes(allIds);
  }, [level1Accounts, level2Accounts]);

  React.useEffect(() => {
    if (addingL1 || addingL2ParentId || addingL3ParentId) {
      setTimeout(() => inlineInputRef.current?.focus(), 50);
    }
  }, [addingL1, addingL2ParentId, addingL3ParentId]);

  React.useEffect(() => {
    if (editingId) setTimeout(() => editInputRef.current?.focus(), 50);
  }, [editingId]);

  const loadData = async () => {
    const [l1Data, l2Data, l3Data] = await Promise.all([
      supabaseSync.fetchChartOfAccountsLevel1(),
      supabaseSync.fetchChartOfAccountsLevel2(),
      supabaseSync.fetchChartOfAccounts(),
    ]);
    setLevel1Accounts(l1Data.map(d => ({ id: d.id, code: d.code, name: d.name, color: d.color })));
    setLevel2Accounts(l2Data.map(d => ({ id: d.id, level1Id: d.level1_id, code: d.code, name: d.name, type: d.type, color: d.color })));
    setLevel3Accounts(l3Data.filter(d => !d.hidden).map(d => ({
      id: d.id, level2Id: d.level2_id, code: d.code, name: d.name, type: d.type, color: d.color, hidden: d.hidden,
    })));
  };

  // === Auto-code generation ===
  const nextL1Code = (): string => {
    const codes = level1Accounts.map(l => parseInt(l.code)).filter(n => !isNaN(n));
    return String((codes.length > 0 ? Math.max(...codes) : 0) + 1);
  };

  const nextL2Code = (l1Id: string): string => {
    const parent = level1Accounts.find(l => l.id === l1Id);
    if (!parent) return "";
    const siblings = level2Accounts.filter(l => l.level1Id === l1Id);
    const suffixes = siblings.map(s => {
      const parts = s.code.split(".");
      return parseInt(parts[parts.length - 1]);
    }).filter(n => !isNaN(n));
    const next = (suffixes.length > 0 ? Math.max(...suffixes) : 0) + 1;
    return `${parent.code}.${next}`;
  };

  const nextL3Code = (l2Id: string): string => {
    const parent = level2Accounts.find(l => l.id === l2Id);
    if (!parent) return "";
    const siblings = level3Accounts.filter(l => l.level2Id === l2Id);
    const suffixes = siblings.map(s => {
      const parts = (s.code || "").split(".");
      return parseInt(parts[parts.length - 1]);
    }).filter(n => !isNaN(n));
    const next = (suffixes.length > 0 ? Math.max(...suffixes) : 0) + 1;
    return `${parent.code}.${next}`;
  };

  // === Recalculate codes after reorder ===
  const recalculateL1Codes = async (items: Level1Data[]) => {
    const updates: Level1Data[] = [];
    for (let i = 0; i < items.length; i++) {
      const newCode = String(i + 1);
      if (items[i].code !== newCode) {
        updates.push({ ...items[i], code: newCode });
      }
    }
    if (updates.length === 0) return items;

    const newL1 = items.map((item, i) => ({ ...item, code: String(i + 1) }));
    setLevel1Accounts(newL1);

    // Update L2 codes that depend on L1
    const newL2 = level2Accounts.map(l2 => {
      const parentL1 = newL1.find(l => l.id === l2.level1Id);
      if (!parentL1) return l2;
      const siblings = level2Accounts.filter(s => s.level1Id === l2.level1Id).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
      const idx = siblings.findIndex(s => s.id === l2.id);
      const newCode = `${parentL1.code}.${idx + 1}`;
      return { ...l2, code: newCode };
    });
    setLevel2Accounts(newL2);

    // Update L3 codes that depend on L2
    const newL3 = level3Accounts.map(l3 => {
      if (!l3.level2Id) return l3;
      const parentL2 = newL2.find(l => l.id === l3.level2Id);
      if (!parentL2) return l3;
      const siblings = level3Accounts.filter(s => s.level2Id === l3.level2Id).sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
      const idx = siblings.findIndex(s => s.id === l3.id);
      const newCode = `${parentL2.code}.${idx + 1}`;
      return { ...l3, code: newCode };
    });
    setLevel3Accounts(newL3);

    // Persist all changes
    for (const l1 of newL1) {
      const orig = items.find(o => o.id === l1.id);
      if (orig && orig.code !== l1.code) {
        await supabaseSync.updateChartOfAccountLevel1(l1.id, { code: l1.code });
      }
    }
    for (const l2 of newL2) {
      const orig = level2Accounts.find(o => o.id === l2.id);
      if (orig && orig.code !== l2.code) {
        await supabaseSync.updateChartOfAccountLevel2(l2.id, { code: l2.code });
      }
    }
    for (const l3 of newL3) {
      const orig = level3Accounts.find(o => o.id === l3.id);
      if (orig && orig.code !== l3.code) {
        await supabaseSync.updateChartOfAccount(l3.id, { code: l3.code });
      }
    }

    return newL1;
  };

  const recalculateL2Codes = async (parentL1Id: string, items: Level2Data[]) => {
    const parent = level1Accounts.find(l => l.id === parentL1Id);
    if (!parent) return;

    const newL2Items = items.map((item, i) => ({ ...item, code: `${parent.code}.${i + 1}` }));
    const otherL2 = level2Accounts.filter(l => l.level1Id !== parentL1Id);
    const allNewL2 = [...otherL2, ...newL2Items];
    setLevel2Accounts(allNewL2);

    // Update L3 codes
    const newL3 = level3Accounts.map(l3 => {
      if (!l3.level2Id) return l3;
      const parentL2 = allNewL2.find(l => l.id === l3.level2Id);
      if (!parentL2) return l3;
      const siblings = level3Accounts.filter(s => s.level2Id === l3.level2Id).sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
      const idx = siblings.findIndex(s => s.id === l3.id);
      return { ...l3, code: `${parentL2.code}.${idx + 1}` };
    });
    setLevel3Accounts(newL3);

    for (const l2 of newL2Items) {
      const orig = level2Accounts.find(o => o.id === l2.id);
      if (orig && (orig.code !== l2.code || orig.level1Id !== l2.level1Id)) {
        await supabaseSync.updateChartOfAccountLevel2(l2.id, { code: l2.code, level1_id: l2.level1Id });
      }
    }
    for (const l3 of newL3) {
      const orig = level3Accounts.find(o => o.id === l3.id);
      if (orig && orig.code !== l3.code) {
        await supabaseSync.updateChartOfAccount(l3.id, { code: l3.code });
      }
    }
  };

  const recalculateL3Codes = async (parentL2Id: string, items: Level3Data[]) => {
    const parent = level2Accounts.find(l => l.id === parentL2Id);
    if (!parent) return;

    const newL3Items = items.map((item, i) => ({ ...item, code: `${parent.code}.${i + 1}` }));
    const otherL3 = level3Accounts.filter(l => l.level2Id !== parentL2Id);
    setLevel3Accounts([...otherL3, ...newL3Items]);

    for (const l3 of newL3Items) {
      const orig = level3Accounts.find(o => o.id === l3.id);
      if (orig && (orig.code !== l3.code || orig.level2Id !== l3.level2Id)) {
        await supabaseSync.updateChartOfAccount(l3.id, { code: l3.code, level2_id: l3.level2Id });
      }
    }
  };

  // === CRUD ===
  const handleAddLevel1 = async () => {
    if (!inlineName.trim()) return;
    const code = nextL1Code();
    const id = await supabaseSync.createChartOfAccountLevel1({ code, name: inlineName.trim() });
    if (id) {
      setLevel1Accounts(prev => [...prev, { id, code, name: inlineName.trim() }]);
      toast({ title: "Nível 1 criado", description: `"${inlineName.trim()}" foi adicionado.` });
    }
    setInlineName("");
    setAddingL1(false);
  };

  const handleAddLevel2 = async (l1Id: string) => {
    if (!inlineName.trim()) return;
    const code = nextL2Code(l1Id);
    const id = await supabaseSync.createChartOfAccountLevel2({
      level1_id: l1Id, code, name: inlineName.trim(), type: "despesa",
    });
    if (id) {
      setLevel2Accounts(prev => [...prev, { id, level1Id: l1Id, code, name: inlineName.trim(), type: "despesa" }]);
      setExpandedNodes(prev => new Set(prev).add(l1Id));
      toast({ title: "Nível 2 criado" });
    }
    setInlineName("");
    setAddingL2ParentId(null);
  };

  const handleAddLevel3 = async (l2Id: string) => {
    if (!inlineName.trim()) return;
    const code = nextL3Code(l2Id);
    const name = inlineName.trim();
    const type = inlineType;
    const id = await addCategory(name, undefined, type, undefined, { level2Id: l2Id, code });
    if (id) {
      setLevel3Accounts(prev => [...prev, { id, level2Id: l2Id, code, name, type }]);
      setExpandedNodes(prev => new Set(prev).add(l2Id));
      toast({ title: "Nível 3 criado" });
    }
    setInlineName("");
    setInlineType("despesa");
    setAddingL3ParentId(null);
  };

  const handleDeleteLevel1 = async (id: string, name: string) => {
    if (level2Accounts.some(l2 => l2.level1Id === id)) {
      toast({ variant: "destructive", title: "Não é possível excluir", description: "Remova os níveis 2 vinculados primeiro." });
      return;
    }
    const success = await supabaseSync.deleteChartOfAccountLevel1(id);
    if (success) {
      setLevel1Accounts(prev => prev.filter(l => l.id !== id));
      toast({ title: "Nível 1 excluído", description: `"${name}" foi removido.` });
    }
  };

  const handleDeleteLevel2 = async (id: string, name: string) => {
    if (level3Accounts.some(l3 => l3.level2Id === id)) {
      toast({ variant: "destructive", title: "Não é possível excluir", description: "Remova os níveis 3 vinculados primeiro." });
      return;
    }
    const success = await supabaseSync.deleteChartOfAccountLevel2(id);
    if (success) {
      setLevel2Accounts(prev => prev.filter(l => l.id !== id));
      toast({ title: "Nível 2 excluído", description: `"${name}" foi removido.` });
    }
  };

  const handleDeleteLevel3 = async (id: string, name: string) => {
    const success = await removeCategory(id);
    if (success) {
      setLevel3Accounts(prev => prev.filter(l => l.id !== id));
    }
  };

  const startEdit = (level: 1 | 2 | 3, id: string, name: string, type?: "receita" | "despesa") => {
    setEditingId(id);
    setEditingLevel(level);
    setEditName(name);
    setEditType(type || "despesa");
  };

  const cancelEdit = () => { setEditingId(null); setEditingLevel(null); setEditName(""); };

  const saveEdit = async () => {
    if (!editingId || !editingLevel || !editName.trim()) return;
    if (editingLevel === 1) {
      const success = await supabaseSync.updateChartOfAccountLevel1(editingId, { name: editName.trim() });
      if (success) setLevel1Accounts(prev => prev.map(l => l.id === editingId ? { ...l, name: editName.trim() } : l));
    } else if (editingLevel === 2) {
      const success = await supabaseSync.updateChartOfAccountLevel2(editingId, { name: editName.trim() });
      if (success) setLevel2Accounts(prev => prev.map(l => l.id === editingId ? { ...l, name: editName.trim() } : l));
    } else if (editingLevel === 3) {
      const success = await supabaseSync.updateChartOfAccount(editingId, { name: editName.trim(), type: editType });
      if (success) {
        setLevel3Accounts(prev => prev.map(l => l.id === editingId ? { ...l, name: editName.trim(), type: editType } : l));
        await forceRefresh();
      }
    }
    toast({ title: "Atualizado com sucesso" });
    cancelEdit();
  };

  const toggleExpand = (id: string) => {
    setExpandedNodes(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const cancelInlineAdd = () => {
    setAddingL1(false);
    setAddingL2ParentId(null);
    setAddingL3ParentId(null);
    setInlineName("");
    setInlineType("despesa");
  };

  // === Drag and drop ===
  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    setActiveId(id);
    if (level1Accounts.find(l => l.id === id)) setActiveDragLevel(1);
    else if (level2Accounts.find(l => l.id === id)) setActiveDragLevel(2);
    else if (level3Accounts.find(l => l.id === id)) setActiveDragLevel(3);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    setActiveDragLevel(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    // L1 reorder
    const activeL1 = level1Accounts.find(l => l.id === activeIdStr);
    const overL1 = level1Accounts.find(l => l.id === overIdStr);
    if (activeL1 && overL1) {
      const sorted = [...level1Accounts].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
      const oldIdx = sorted.findIndex(l => l.id === activeIdStr);
      const newIdx = sorted.findIndex(l => l.id === overIdStr);
      if (oldIdx !== -1 && newIdx !== -1) {
        const reordered = [...sorted];
        const [moved] = reordered.splice(oldIdx, 1);
        reordered.splice(newIdx, 0, moved);
        await recalculateL1Codes(reordered);
        toast({ title: "Ordem atualizada" });
      }
      return;
    }

    // L2 drag (same or cross-parent)
    const activeL2 = level2Accounts.find(l => l.id === activeIdStr);
    const overL2 = level2Accounts.find(l => l.id === overIdStr);
    if (activeL2 && overL2) {
      if (activeL2.level1Id === overL2.level1Id) {
        // Same parent reorder
        const siblings = level2Accounts
          .filter(l => l.level1Id === activeL2.level1Id)
          .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
        const oldIdx = siblings.findIndex(l => l.id === activeIdStr);
        const newIdx = siblings.findIndex(l => l.id === overIdStr);
        if (oldIdx !== -1 && newIdx !== -1) {
          const reordered = [...siblings];
          const [moved] = reordered.splice(oldIdx, 1);
          reordered.splice(newIdx, 0, moved);
          await recalculateL2Codes(activeL2.level1Id, reordered);
          toast({ title: "Ordem atualizada" });
        }
      } else {
        // Cross-parent: move activeL2 to overL2's parent
        const oldParentId = activeL2.level1Id;
        const newParentId = overL2.level1Id;

        const movedItem = { ...activeL2, level1Id: newParentId };

        const newParentSiblings = level2Accounts
          .filter(l => l.level1Id === newParentId && l.id !== activeIdStr)
          .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
        const overIdx = newParentSiblings.findIndex(l => l.id === overIdStr);
        newParentSiblings.splice(overIdx + 1, 0, movedItem);

        const oldParentSiblings = level2Accounts
          .filter(l => l.level1Id === oldParentId && l.id !== activeIdStr)
          .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

        const otherL2 = level2Accounts.filter(l => l.level1Id !== oldParentId && l.level1Id !== newParentId);
        const allL2 = [...otherL2, ...oldParentSiblings, ...newParentSiblings];
        setLevel2Accounts(allL2);

        await recalculateL2Codes(newParentId, newParentSiblings);
        await recalculateL2Codes(oldParentId, oldParentSiblings);
        toast({ title: "Movido para outro grupo" });
      }
      return;
    }

    // L3 drag (same or cross-parent, or onto L2/L1)
    const activeL3 = level3Accounts.find(l => l.id === activeIdStr);
    if (activeL3 && activeL3.level2Id) {
      const overL3 = level3Accounts.find(l => l.id === overIdStr);
      const overL2Target = level2Accounts.find(l => l.id === overIdStr);
      const overL1Target = level1Accounts.find(l => l.id === overIdStr);

      let targetL2Id: string | null = null;
      let insertAfterIdx = -1;

      if (overL3 && overL3.level2Id) {
        targetL2Id = overL3.level2Id;
        if (targetL2Id === activeL3.level2Id) {
          const siblings = level3Accounts
            .filter(l => l.level2Id === activeL3.level2Id)
            .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
          const oldIdx = siblings.findIndex(l => l.id === activeIdStr);
          const newIdx = siblings.findIndex(l => l.id === overIdStr);
          if (oldIdx !== -1 && newIdx !== -1) {
            const reordered = [...siblings];
            const [moved] = reordered.splice(oldIdx, 1);
            reordered.splice(newIdx, 0, moved);
            await recalculateL3Codes(activeL3.level2Id, reordered);
            toast({ title: "Ordem atualizada" });
          }
          return;
        }
        const newParentSiblings = level3Accounts
          .filter(l => l.level2Id === targetL2Id && l.id !== activeIdStr)
          .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
        const overIdx = newParentSiblings.findIndex(l => l.id === overIdStr);
        insertAfterIdx = overIdx;
      } else if (overL2Target) {
        targetL2Id = overL2Target.id;
        setExpandedNodes(prev => new Set(prev).add(targetL2Id!));
      } else if (overL1Target) {
        const firstL2 = level2Accounts
          .filter(l => l.level1Id === overL1Target.id)
          .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }))[0];
        if (firstL2) {
          targetL2Id = firstL2.id;
          setExpandedNodes(prev => { const s = new Set(prev); s.add(overL1Target.id); s.add(firstL2.id); return s; });
        } else {
          toast({ title: "Sem Nível 2", description: "Crie um Nível 2 neste grupo antes de mover.", variant: "destructive" });
          return;
        }
      }

      if (!targetL2Id || targetL2Id === activeL3.level2Id) return;

      const oldParentId = activeL3.level2Id;
      const movedItem = { ...activeL3, level2Id: targetL2Id };

      const newParentSiblings = level3Accounts
        .filter(l => l.level2Id === targetL2Id && l.id !== activeIdStr)
        .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));

      if (insertAfterIdx >= 0) {
        newParentSiblings.splice(insertAfterIdx + 1, 0, movedItem);
      } else {
        newParentSiblings.push(movedItem);
      }

      const oldParentSiblings = level3Accounts
        .filter(l => l.level2Id === oldParentId && l.id !== activeIdStr)
        .sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));

      const otherL3 = level3Accounts.filter(l => l.level2Id !== oldParentId && l.level2Id !== targetL2Id);
      setLevel3Accounts([...otherL3, ...oldParentSiblings, ...newParentSiblings]);

      await supabaseSync.updateChartOfAccount(activeIdStr, { level2_id: targetL2Id });
      await recalculateL3Codes(targetL2Id, newParentSiblings);
      await recalculateL3Codes(oldParentId, oldParentSiblings);
      toast({ title: "Movido para outro grupo" });
      return;
    }
  };

  // === Hierarchy tree building ===
  const hierarchyTree = React.useMemo(() => {
    const sortedL1 = [...level1Accounts].sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
    return sortedL1.map(l1 => {
      const l2Items = level2Accounts.filter(l2 => l2.level1Id === l1.id).sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
      return {
        ...l1, level: 1 as const,
        children: l2Items.map(l2 => {
          const l3Items = level3Accounts.filter(l3 => l3.level2Id === l2.id).sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
          return { ...l2, level: 2 as const, children: l3Items.map(l3 => ({ ...l3, level: 3 as const })) };
        }),
      };
    });
  }, [level1Accounts, level2Accounts, level3Accounts]);

  const filteredTree = React.useMemo(() => {
    const term = hierarchySearch.trim().toLowerCase();
    if (!term) return hierarchyTree;
    return hierarchyTree.map(l1 => {
      const l1Match = l1.name.toLowerCase().includes(term) || l1.code.toLowerCase().includes(term);
      const filteredL2 = l1.children.map(l2 => {
        const l2Match = l2.name.toLowerCase().includes(term) || l2.code.toLowerCase().includes(term);
        const filteredL3 = l2.children.filter(l3 => l3.name.toLowerCase().includes(term) || (l3.code || "").toLowerCase().includes(term));
        if (l2Match || filteredL3.length > 0) return { ...l2, children: filteredL3.length > 0 ? filteredL3 : l2.children };
        return null;
      }).filter(Boolean) as typeof l1.children;
      if (l1Match || filteredL2.length > 0) return { ...l1, children: filteredL2.length > 0 ? filteredL2 : l1.children };
      return null;
    }).filter(Boolean) as typeof hierarchyTree;
  }, [hierarchyTree, hierarchySearch]);

  React.useEffect(() => {
    if (hierarchySearch.trim()) {
      const allIds = new Set<string>();
      filteredTree.forEach(l1 => { allIds.add(l1.id); l1.children.forEach(l2 => allIds.add(l2.id)); });
      setExpandedNodes(allIds);
    }
  }, [hierarchySearch, filteredTree]);

  const hasNoViewPermission = !permLoading && !canView();

  if (hasNoViewPermission) {
    return (
      <Card>
        <CardHeader><CardTitle>Plano de Contas Hierárquico</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <p>Você não tem permissão para visualizar este módulo.</p>
            <p className="text-sm mt-2">Solicite ao proprietário da organização que altere suas permissões.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Drag overlay content
  const getDragOverlayContent = () => {
    if (!activeId) return null;
    const l1 = level1Accounts.find(l => l.id === activeId);
    if (l1) return <div className="bg-background border rounded px-3 py-2 shadow-lg font-bold">{l1.code} - {l1.name}</div>;
    const l2 = level2Accounts.find(l => l.id === activeId);
    if (l2) return <div className="bg-background border rounded px-3 py-2 shadow-lg font-semibold text-sm">{l2.code} - {l2.name}</div>;
    const l3 = level3Accounts.find(l => l.id === activeId);
    if (l3) return <div className="bg-background border rounded px-3 py-2 shadow-lg text-sm">{l3.code} - {l3.name}</div>;
    return null;
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between flex-wrap gap-2">
            <span>Plano de Contas</span>
            <span className="text-sm font-normal text-muted-foreground">
              {level1Accounts.length + level2Accounts.length + level3Accounts.length} registros
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[150px] md:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Pesquisar..." value={hierarchySearch} onChange={e => setHierarchySearch(e.target.value)} className="pl-9" />
            </div>
            <Button size="sm" onClick={() => { cancelInlineAdd(); setAddingL1(true); }}>
              <Plus className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Adicionar Nível 1</span>
            </Button>
            <ImportChartOfAccountsButton onImportSuccess={loadData} />
            <Button variant="outline" size="sm" onClick={() => exportToExcel({ level1Accounts, level2Accounts, level3Accounts })} disabled={level3Accounts.length === 0}>
              <FileSpreadsheet className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">Excel</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportToPDF({ level1Accounts, level2Accounts, level3Accounts })} disabled={level3Accounts.length === 0}>
              <FileText className="h-4 w-4 md:mr-1" />
              <span className="hidden md:inline">PDF</span>
            </Button>
          </div>

          <ScrollArea className="h-[60vh] md:h-[800px] border rounded-md p-2 md:p-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxisModifier]}
            >
              {filteredTree.length > 0 ? (
                <SortableContext items={filteredTree.map(l1 => l1.id)} strategy={verticalListSortingStrategy}>
                  {filteredTree.map(l1 => (
                    <SortableL1
                      key={l1.id}
                      l1={l1}
                      expandedNodes={expandedNodes}
                      toggleExpand={toggleExpand}
                      editingId={editingId}
                      editName={editName}
                      setEditName={setEditName}
                      editType={editType}
                      setEditType={setEditType}
                      editInputRef={editInputRef}
                      saveEdit={saveEdit}
                      cancelEdit={cancelEdit}
                      startEdit={startEdit}
                      handleDeleteLevel1={handleDeleteLevel1}
                      handleDeleteLevel2={handleDeleteLevel2}
                      handleDeleteLevel3={handleDeleteLevel3}
                      addingL2ParentId={addingL2ParentId}
                      setAddingL2ParentId={setAddingL2ParentId}
                      addingL3ParentId={addingL3ParentId}
                      setAddingL3ParentId={setAddingL3ParentId}
                      setExpandedNodes={setExpandedNodes}
                      cancelInlineAdd={cancelInlineAdd}
                      handleAddLevel2={handleAddLevel2}
                      handleAddLevel3={handleAddLevel3}
                      inlineName={inlineName}
                      setInlineName={setInlineName}
                      inlineType={inlineType}
                      setInlineType={setInlineType}
                      inlineInputRef={inlineInputRef}
                      transactionCounts={transactionCounts}
                    />
                  ))}
                  {addingL1 && (
                    <div className="pl-1 mt-1">
                      <InlineInput
                        inlineName={inlineName}
                        setInlineName={setInlineName}
                        inlineType={inlineType}
                        setInlineType={setInlineType}
                        inputRef={inlineInputRef}
                        onSave={handleAddLevel1}
                        onCancel={cancelInlineAdd}
                      />
                    </div>
                  )}
                </SortableContext>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  {hierarchySearch.trim() ? "Nenhum resultado encontrado" : "Nenhum registro cadastrado. Clique em \"Adicionar Plano de Contas Nível 1\" para começar."}
                </div>
              )}
              <DragOverlay>{getDragOverlayContent()}</DragOverlay>
            </DndContext>
          </ScrollArea>
        </CardContent>
      </Card>

      <PermissionAlert open={showAlert} onOpenChange={setShowAlert} action={alertAction} />
    </div>
  );
};
